import type { CanonicalLessonProgressLesson, GuidedLessonResponse } from '../api/lezione';
import type { ArgomentoGuidedProgress, QuizAnswer } from './guided-lesson-progress';
import type { LessonReadRecord } from './lesson-read-progress';

export type LessonProgressState =
  | 'unopened'
  | 'opened_read_only'
  | 'in_progress_with_answers'
  | 'completed_with_errors'
  | 'completed_all_correct'
  | 'ripassa_attempt';

export interface GuidedLessonProgressSummary {
  state: LessonProgressState;
  answeredCount: number;
  correctCount: number;
  wrongCount: number;
  requiredQuizCount: number;
  completedRead: boolean;
}

export interface GuidedFinishCopy {
  title: string;
  body: string;
  detail: string;
  tone: 'neutral' | 'success' | 'review';
}

export type CanonicalLessonState = 'not_started' | 'in_progress' | 'completed';

export interface CanonicalLessonMicroTopic {
  microTopicId: string;
  nameIt: string;
  sortOrder: number;
  principleIds: string[];
  fastState: 'taught' | 'untaught';
}

export interface CanonicalLessonRow {
  argomentoId: string;
  lessonId: string;
  order: number;
  title: string;
  resumePath: string;
  state: CanonicalLessonState;
  activeAttemptId: string | null;
  currentSourceChapterOrder: number | null;
  latestAttempts: CanonicalLessonProgressLesson['latestAttempts'];
  principleIds: string[];
  sourceMicroTopicId: string;
}

export function buildCanonicalLessonRows(input: {
  argomentoId: string;
  microTopics: CanonicalLessonMicroTopic[];
  readByMicroTopic: Record<string, LessonReadRecord>;
  guidedCompleted: boolean;
  guidedOpened: boolean;
  progressLessons?: CanonicalLessonProgressLesson[];
}): CanonicalLessonRow[] {
  const progressByLesson = new Map(
    (input.progressLessons ?? [])
      .filter((lesson) => lesson.argomentoId === input.argomentoId)
      .map((lesson) => [lesson.lessonId, lesson]),
  );
  return [...input.microTopics]
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((mt, index) => {
      const canonical = progressByLesson.get(mt.microTopicId);
      const state: CanonicalLessonState =
        canonical?.status === 'in_progress'
          ? 'in_progress'
          : canonical?.status === 'completed'
            ? 'completed'
            : 'not_started';
      void input.readByMicroTopic;
      void input.guidedCompleted;
      void input.guidedOpened;
      return {
        argomentoId: input.argomentoId,
        lessonId: mt.microTopicId,
        order: Number.isFinite(mt.sortOrder) && mt.sortOrder > 0 ? mt.sortOrder : index + 1,
        title: mt.nameIt,
        resumePath: `/studia/lezione/guidata/${input.argomentoId}/${mt.microTopicId}`,
        state,
        activeAttemptId: canonical?.activeAttemptId ?? null,
        currentSourceChapterOrder: canonical?.currentSourceChapterOrder ?? null,
        latestAttempts: canonical?.latestAttempts ?? [],
        principleIds: mt.principleIds,
        sourceMicroTopicId: mt.microTopicId,
      };
    });
}

export function argomentoStudentNumber(argomentoId: string | undefined | null, sortOrder: number): number {
  const fromId = /^arg(\d+)$/i.exec(argomentoId ?? '')?.[1];
  if (fromId) return Number(fromId);
  if (Number.isFinite(sortOrder) && sortOrder > 0) return sortOrder;
  if (Number.isFinite(sortOrder)) return sortOrder + 1;
  return 0;
}

export function formatArgomentoNumber(argomentoId: string | undefined | null, sortOrder: number): string {
  const n = argomentoStudentNumber(argomentoId, sortOrder);
  return n > 0 ? String(n).padStart(2, '0') : '--';
}

export function normalizeLessonAssetRef(ref: string): string {
  const trimmed = ref.trim().replace(/^\/+/, '');
  if (!trimmed) return trimmed;
  if (trimmed.includes('/')) return trimmed;
  const numeric = /^(\d{1,4})(?:\.png)?$/i.exec(trimmed);
  if (numeric) return `img_sign/${numeric[1]}.png`;
  return `img_sign_by_hash/${trimmed.endsWith('.png') ? trimmed : `${trimmed}.png`}`;
}

export function lessonAssetSrc(ref: string): string {
  const normalized = normalizeLessonAssetRef(ref);
  return normalized.startsWith('/') ? normalized : `/${normalized}`;
}

export function visibleAssetRefsForChapter(chapter: GuidedLessonResponse['chapters'][number] | null | undefined): string[] {
  if (!chapter) return [];
  const refs = new Set<string>();
  for (const ref of chapter.lessonImageRefs ?? chapter.signImageRefs ?? []) {
    if (ref) refs.add(normalizeLessonAssetRef(ref));
  }
  for (const segment of chapter.segments ?? []) {
    if (segment.kind !== 'block') continue;
    if (segment.image_ref) refs.add(normalizeLessonAssetRef(segment.image_ref));
    for (const ref of segment.alt_image_refs ?? []) {
      if (ref) refs.add(normalizeLessonAssetRef(ref));
    }
  }
  return Array.from(refs);
}

export function questionRefsForChapter(chapter: GuidedLessonResponse['chapters'][number] | null | undefined): string[] {
  if (!chapter) return [];
  return chapter.segments
    .filter((segment): segment is Extract<(typeof chapter.segments)[number], { kind: 'quiz' }> => segment.kind === 'quiz')
    .map((segment) => segment.quiz.practice_question_id)
    .filter((id): id is string => Boolean(id));
}

export function countGuidedQuizAnswers(answers: Record<string, QuizAnswer> | undefined | null): {
  answeredCount: number;
  correctCount: number;
  wrongCount: number;
} {
  const values = Object.values(answers ?? {});
  const answeredCount = values.length;
  const correctCount = values.filter((answer) => answer.correct).length;
  return {
    answeredCount,
    correctCount,
    wrongCount: answeredCount - correctCount,
  };
}

export function deriveGuidedLessonProgressState(
  progress: ArgomentoGuidedProgress | undefined,
  requiredQuizCount: number,
): GuidedLessonProgressSummary {
  const counts = countGuidedQuizAnswers(progress?.answers);
  const completedRead = progress?.completed === true;
  const required = Math.max(0, requiredQuizCount);
  const answeredRequired = required > 0 && counts.answeredCount >= required;
  if (!progress || ((progress.currentChapter ?? 0) <= 1 && counts.answeredCount === 0 && !completedRead)) {
    return { state: 'unopened', requiredQuizCount: required, completedRead, ...counts };
  }
  if (completedRead && answeredRequired && counts.wrongCount === 0) {
    return { state: 'completed_all_correct', requiredQuizCount: required, completedRead, ...counts };
  }
  if (completedRead && answeredRequired) {
    return { state: 'completed_with_errors', requiredQuizCount: required, completedRead, ...counts };
  }
  if (counts.answeredCount > 0) {
    return { state: 'in_progress_with_answers', requiredQuizCount: required, completedRead, ...counts };
  }
  return { state: 'opened_read_only', requiredQuizCount: required, completedRead, ...counts };
}

export function guidedLessonTitle(lesson: GuidedLessonResponse | null | undefined): string {
  return lesson?.lesson_title
    ?? lesson?.argomento_name
    ?? lesson?.chapters?.[0]?.title
    ?? 'Lezione';
}

export function guidedFinishCopy(
  summary: GuidedLessonProgressSummary,
  lessonTitle: string,
  chapterCount: number,
): GuidedFinishCopy {
  const quizDetail = summary.requiredQuizCount > 0
    ? `${summary.answeredCount}/${summary.requiredQuizCount} domande risposte`
    : 'Nessuna domanda integrata';
  if (summary.answeredCount === 0) {
    return {
      title: 'Lezione letta',
      body: `Hai letto ${lessonTitle}. La preparazione si aggiorna solo quando rispondi alle domande integrate.`,
      detail: `${chapterCount} capitoli letti · ${quizDetail}`,
      tone: 'neutral',
    };
  }
  if (summary.state === 'completed_all_correct') {
    return {
      title: 'Completata senza errori',
      body: `Hai completato ${lessonTitle} e hai risposto correttamente alle domande integrate.`,
      detail: `${chapterCount} capitoli letti · ${quizDetail}`,
      tone: 'success',
    };
  }
  if (summary.state === 'completed_with_errors') {
    return {
      title: 'Lezione completata',
      body: `Hai completato ${lessonTitle}. Alcune risposte vanno riviste nei punti chiave o con Il Prof.`,
      detail: `${chapterCount} capitoli letti · ${summary.wrongCount} ${summary.wrongCount === 1 ? 'errore' : 'errori'}`,
      tone: 'review',
    };
  }
  return {
    title: 'Lezione letta',
    body: `Hai letto ${lessonTitle}. Riprendi le domande integrate per completare il controllo.`,
    detail: `${chapterCount} capitoli letti · ${quizDetail}`,
    tone: 'neutral',
  };
}
